frappe.provide("erpnext.accounts"); // @dokos

/**
 * Payment collection wiring for Sales Invoice, Sales Order and Subscription. // @dokos
 *
 * Adds a single functional "Request Payment" entry point and renders the payment
 * lifecycle (link sent / awaiting / paid / failed) in the dedicated
 * "Payment Collection" tab — so users never have to navigate the technical
 * Payment Request document. These handlers are *additive*: they stack on top of
 * the doctype's own form scripts rather than replacing them.
 */

const PR_CONTEXT_METHOD =
	"erpnext.accounts.doctype.payment_request.payment_request.get_payment_request_context";

// @dokos: request statuses are coloured by the shared status map
// (erpnext.dokos.ui.status_theme), not by a local indicator table.

erpnext.accounts.setup_payment_collection = function (frm) {
	if (frm.is_new() || frm.doc.is_return) return;

	// Sales Invoice / Sales Order only make sense once submitted; Subscription is
	// not submittable, so a saved document is enough.
	if (["Sales Invoice", "Sales Order"].includes(frm.doctype) && frm.doc.docstatus !== 1) {
		return;
	}

	frappe.call({
		method: PR_CONTEXT_METHOD,
		args: { reference_doctype: frm.doctype, reference_name: frm.doc.name },
		callback: ({ message }) => {
			if (!message || !message.eligible) {
				erpnext.accounts._render_payment_section(frm, message);
				return;
			}
			erpnext.accounts._add_request_button(frm, message);
			erpnext.accounts._render_payment_section(frm, message);
		},
	});
};

erpnext.accounts._open_panel = function (frm, context) {
	new erpnext.accounts.PaymentRequestPanel({ frm, context }).show();
};

erpnext.accounts._add_request_button = function (frm, context) {
	if (!context.can_create || flt(context.amount_due) <= 0) return;
	if (!frappe.boot.user.in_create.includes("Payment Request")) return;

	frm.add_custom_button(
		__("Request Payment"),
		() => erpnext.accounts._open_panel(frm, context),
		__("Create")
	);
};

erpnext.accounts._render_payment_section = function (frm, context) {
	const field = frm.get_field("dokos_payment_request_status");
	if (!field || !field.$wrapper) return;

	const $wrap = field.$wrapper.empty();
	const requests = (context && context.payment_requests) || [];
	const ui = erpnext.dokos.ui; // @dokos: shared Espresso façade

	if (!requests.length) {
		// @dokos: the shared empty state — a title, one sentence naming what would be requested,
		// and the "Request Payment" action when the user may create one.
		const eligible = context && context.eligible && flt(context.amount_due) > 0;
		$wrap.append(
			ui.empty_state({
				icon: "credit-card",
				title: __("No payment requested yet"),
				description: eligible
					? __("Send a payment link for the {0} still due on this document.", [
							format_currency(flt(context.amount_due), context.currency),
					  ])
					: __("Payment requests and gateway activity for this document will appear here."),
				actions:
					eligible && context.can_create
						? [
								{
									label: __("Request Payment"),
									variant: "solid",
									icon: "plus",
									onclick: () => erpnext.accounts._open_panel(frm, context),
								},
						  ]
						: [],
			})
		);
		return;
	}

	const latest = requests[0];

	// @dokos: a dk-card with the status pill and amount, a meta line and one row of buttons —
	// no inline styles, no legacy indicator-pill.
	const $card = $('<div class="dk-card prp-status-card"><div class="dk-card__body"></div></div>');
	const $body = $card.find(".dk-card__body");
	$card.appendTo($wrap);

	$('<div class="dk-row">')
		.append(
			ui.pill({ label: __(latest.status || "Draft"), status: latest.status || "Draft" }),
			$('<span class="dk-num">').text(
				format_currency(latest.grand_total, latest.currency || context.currency)
			)
		)
		.appendTo($body);

	if (requests.length > 1) {
		$('<div class="dk-muted">')
			.text(__("{0} payment requests — latest shown above.", [requests.length]))
			.appendTo($body);
	}

	const $actions = $('<div class="dk-row prp-status-actions">').appendTo($body);
	const btn = (opts) => ui.button(Object.assign({ size: "sm" }, opts));

	if (latest.payment_url) {
		$actions.append(
			btn({
				label: __("Copy payment link"),
				icon: "copy",
				onclick: () => frappe.utils.copy_to_clipboard(latest.payment_url),
			})
		);
	}

	const is_open = !["Paid", "Completed", "Cancelled"].includes(latest.status);

	if (is_open && latest.email_to && !latest.mute_email) {
		$actions.append(
			btn({
				label: __("Resend email"),
				icon: "mail",
				// returning the promise keeps the button busy until the email is queued
				onclick: () =>
					frappe
						.xcall(
							"erpnext.accounts.doctype.payment_request.payment_request.resend_payment_email",
							{
								docname: latest.name,
							}
						)
						.then(() => frappe.show_alert({ message: __("Email sent"), indicator: "green" })),
			})
		);
	}

	$actions.append(
		btn({
			label: __("View request"),
			variant: "ghost",
			onclick: () => frappe.set_route("Form", "Payment Request", latest.name),
		})
	);

	if (is_open && latest.docstatus === 1) {
		$actions.append(
			btn({
				label: __("Cancel request"),
				variant: "ghost",
				theme: "red",
				onclick: () => {
					frappe.confirm(__("Cancel this payment request?"), () => {
						frappe
							.xcall("frappe.client.cancel", {
								doctype: "Payment Request",
								name: latest.name,
							})
							.then(() => {
								frappe.show_alert({ message: __("Request cancelled"), indicator: "orange" });
								erpnext.accounts.setup_payment_collection(frm);
							});
					});
				},
			})
		);
	}
};

["Sales Invoice", "Sales Order", "Subscription"].forEach((doctype) => {
	frappe.ui.form.on(doctype, {
		refresh(frm) {
			erpnext.accounts.setup_payment_collection(frm);
		},
	});
});
